import { useEffect, useMemo, useRef, useState } from 'react';

const DESKTOP_MIN_WIDTH = 1024;
const MIN_TABLE_SCROLL_HEIGHT = 180;
const TABLE_BOTTOM_GAP = 0;
const TABLE_HEADER_FALLBACK_HEIGHT = 56;

export const useDesktopTableScroll = (dependencies = []) => {
    const tableContentRef = useRef(null);
    const footerRef = useRef(null);
    const [scrollY, setScrollY] = useState(undefined);

    useEffect(() => {
        let frameRequestId = null;

        const updateScrollHeight = () => {
            if (typeof window === 'undefined') {
                return;
            }

            if (window.innerWidth < DESKTOP_MIN_WIDTH) {
                setScrollY(undefined);
                return;
            }

            const tableContentElement = tableContentRef.current;
            if (!tableContentElement) {
                setScrollY(undefined);
                return;
            }

            const tableTop = tableContentElement.getBoundingClientRect().top;
            const footerTop = footerRef.current?.getBoundingClientRect().top ?? window.innerHeight;
            const tableHeaderElement = tableContentElement.querySelector('.ant-table-thead');
            const tableHeaderHeight = Math.ceil(
                tableHeaderElement?.getBoundingClientRect().height || TABLE_HEADER_FALLBACK_HEIGHT
            );
            const availableBodyHeight = Math.floor(
                footerTop - tableTop - tableHeaderHeight - TABLE_BOTTOM_GAP
            );
            const nextHeight = Math.max(availableBodyHeight, MIN_TABLE_SCROLL_HEIGHT);

            setScrollY(nextHeight);
        };

        const scheduleUpdate = () => {
            if (frameRequestId) {
                window.cancelAnimationFrame(frameRequestId);
            }

            frameRequestId = window.requestAnimationFrame(updateScrollHeight);
        };

        scheduleUpdate();
        window.addEventListener('resize', scheduleUpdate);

        return () => {
            if (frameRequestId) {
                window.cancelAnimationFrame(frameRequestId);
            }
            window.removeEventListener('resize', scheduleUpdate);
        };
    }, dependencies);

    const tableScrollConfig = useMemo(() => {
        if (!scrollY) {
            return { x: 'max-content' };
        }

        return { x: 'max-content', y: scrollY };
    }, [scrollY]);

    return { tableContentRef, footerRef, tableScrollConfig };
};
